import getAccountNumber from "../../utils/getAccountNumber";
import getPostCode from "../../utils/getPostCode";
import { createAction } from "./PriceBlockReducer";
import {
  BRANCH_SELECTED,
  FETCH_BRANCH_STOCK_FAILURE,
  FETCH_BRANCH_STOCK_STARTED,
  FETCH_BRANCH_STOCK_SUCCESS,
  FETCH_CLEARANCE_PRICE_FAILED,
  FETCH_CLEARANCE_PRICE_STARTED,
  FETCH_CLEARANCE_PRICE_SUCCESS,
  FETCH_PRICES_FAILURE,
  FETCH_PRICES_STARTED,
  FETCH_PRICES_SUCCESS,
  FETCH_STOCK_FAILURE,
  FETCH_STOCK_STARTED,
  FETCH_STOCK_SUCCESS,
  FETCH_SUBURB,
  GST_TYPE_TOGGLED,
  POST_CODE_UPDATED,
  PRICE_DETAILS_UPDATED,
  PRICE_TYPE_TOGGLED,
  QUANTITY_DECREMENTED,
  QUANTITY_INCREMENTED,
  QUANTITY_UPDATED,
  STOCK_DETAILS_UPDATED,
  UNIT_OF_MEASURE_UPDATED,
  WISHLIST_TOGGLED,
} from "./PriceBlockActionTypes";
import toggleWishlistProduct from "../../utils/toggleWishlistProduct";
import isProductInWishlist from "../../utils/isProductInWishlist";

const createInstance = (
  priceService,
  templateContext,
  stateManager,
  stockService,
  stockBranchesService,
  suburbServiceClient,
  clearancePriceService
) => {
  const handlePostCodeUpdate = async (postCode) => {
    stateManager.dispatch(
      createAction(POST_CODE_UPDATED, postCode),
      createAction(FETCH_PRICES_STARTED)
    );

    try {
      const priceList = await priceService.fetchPrice(
        getAccountNumber(),
        postCode,
        templateContext.productCode
      );
      stateManager.dispatch(
        createAction(FETCH_PRICES_SUCCESS, priceList),
        createAction(PRICE_DETAILS_UPDATED)
      );
    } catch (error) {
      stateManager.dispatch(createAction(FETCH_PRICES_FAILURE, error));
    }
  };

  const handleInitialPageLoad = async (initialState) => {
    if (!initialState.isPostCodeUpdateRequired) {
      stateManager.dispatch(createAction(FETCH_PRICES_STARTED));
      try {
        const priceList = await priceService.fetchPrice(
          getAccountNumber(),
          getPostCode(),
          templateContext.productCode
        );
        stateManager.dispatch(
          createAction(FETCH_PRICES_SUCCESS, priceList),
          createAction(PRICE_DETAILS_UPDATED)
        );
      } catch (error) {
        stateManager.dispatch(createAction(FETCH_PRICES_FAILURE, error));
      }
    }
  };

  const handleClearancePriceRequest = async (gstIncludePrice) => {
    stateManager.dispatch(createAction(FETCH_CLEARANCE_PRICE_STARTED));

    try {
      const clearancePrice = await clearancePriceService.fetchPrices(
        templateContext.productCode
      );
      stateManager.dispatch(
        createAction(FETCH_CLEARANCE_PRICE_SUCCESS, {
          clearancePrice: clearancePrice,
          gstIncludePrice: gstIncludePrice,
        })
      );
    } catch (error) {
      stateManager.dispatch(createAction(FETCH_CLEARANCE_PRICE_FAILED, error));
    }
  };

  const handleUnitOfMeasureChange = (unitOfMeasure) => {
    stateManager.dispatch(
      createAction(UNIT_OF_MEASURE_UPDATED, unitOfMeasure),
      createAction(PRICE_DETAILS_UPDATED),
      createAction(STOCK_DETAILS_UPDATED)
    );
  };

  const handleGstToggle = () => {
    stateManager.dispatch(
      createAction(GST_TYPE_TOGGLED),
      createAction(PRICE_DETAILS_UPDATED)
    );
  };

  const handleWishlistToggle = () => {
    toggleWishlistProduct(templateContext.productCode);
    stateManager.dispatch(
      createAction(
        WISHLIST_TOGGLED,
        isProductInWishlist(templateContext.productCode)
      )
    );
  };

  const handlePriceTypeToggle = (priceType) => {
    stateManager.dispatch(
      createAction(PRICE_TYPE_TOGGLED, priceType),
      createAction(PRICE_DETAILS_UPDATED)
    );
  };

  const handleQuantityDecrement = () => {
    stateManager.dispatch(createAction(QUANTITY_DECREMENTED));
  };

  const handleQuantityIncrement = () => {
    stateManager.dispatch(createAction(QUANTITY_INCREMENTED));
  };

  const handleQuantityChange = (quantity) => {
    stateManager.dispatch(createAction(QUANTITY_UPDATED, quantity));
  };

  const handleStockAvailabilityQuery = async (quantity, branchId) => {
    stateManager.dispatch(createAction(FETCH_STOCK_STARTED));
    try {
      const productStockAvailabilityStatus =
        await stockService.fetchStockAvailability(
          templateContext.productCode,
          quantity,
          branchId
        );
      stateManager.dispatch(
        createAction(FETCH_STOCK_SUCCESS, productStockAvailabilityStatus),
        createAction(STOCK_DETAILS_UPDATED)
      );
    } catch (error) {
      stateManager.dispatch(createAction(FETCH_STOCK_FAILURE, error));
    }
  };

  const handleStockAvailabilityBranchesQuery = async (
    quantity,
    unitOfMeasure,
    location
  ) => {
    stateManager.dispatch(createAction(FETCH_BRANCH_STOCK_STARTED));
    try {
      const stockAvailabilityBranchesList =
        await stockBranchesService.fetchStockAvailabilityBranches(
          templateContext.productCode,
          quantity,
          unitOfMeasure,
          location
        );
      stateManager.dispatch(
        createAction(FETCH_BRANCH_STOCK_SUCCESS, stockAvailabilityBranchesList)
      );
    } catch (error) {
      stateManager.dispatch(createAction(FETCH_BRANCH_STOCK_FAILURE, error));
    }
  };

  const handleSuburbQuery = async (value) => {
    try {
      const suburbsList = await suburbServiceClient.fetchSuburbs(value);
      stateManager.dispatch(createAction(FETCH_SUBURB, suburbsList));
    } catch (error) {
      return error;
    }
  };

  const handleSelectedBranch = (branchSelected) => {
    stateManager.dispatch(createAction(BRANCH_SELECTED, branchSelected));
  };

  return {
    handlePostCodeUpdate,
    handleInitialPageLoad,
    handleUnitOfMeasureChange,
    handleGstToggle,
    handleWishlistToggle,
    handlePriceTypeToggle,
    handleQuantityDecrement,
    handleQuantityIncrement,
    handleQuantityChange,
    handleStockAvailabilityQuery,
    handleStockAvailabilityBranchesQuery,
    handleSuburbQuery,
    handleClearancePriceRequest,
    handleSelectedBranch,
  };
};

export default {
  createInstance,
};
